/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv, applyPatch } from 'mobx-state-tree';

import { adoptFunctions, constructProps } from './utils';

const CoreModelClass = require('gemini/lib/@waca/dashboard-common/dist/core/Model');

/**
 * The event that will be sent on a model change to who ever is listening to model changes
 * This is to support the legacy Model
 * @param {string} eventName the name of the triggered event e.g. change:title
 * @param {object} patch JSON patch for the change
 * @param {object} revPatch reverse JSON patch for the change (does the undo)
 * @param {string} modelId model identifier
 */
export const _LegacyEvent$1 = function (eventName, patch, revPatch, modelId) {
	this.eventName = eventName;
	this.name = eventName;
	this.value = patch;
	this.prevValue = revPatch;
	this.model = modelId;
	this.sender = modelId;
	this.senderContext = {};
	this.collection = null;
	this.data = {};
};


export const ModelAdapter = types.model({})
	.volatile(self => ({
		attr: {},
		options: {},

		localizedProps: [],
		whitelistAttrs: [],
		nestedCollections: {},
		runtimeAttrs: [],
		nestedModels: {},
		contentReferences: [],
		colorProperties: [],

		_events: {},

		_lastSnapshot: null,
		_setValueCalled: false,


		//actions added will be responsible to trigger legacy events
		//this is a generic helper to trigger a legacy event
		triggerLegacyEvent: function (eventName, patch, reversePatch) {
			const event = new _LegacyEvent$1(eventName, patch, reversePatch, self.id);
			self.trigger(eventName, event);
		}
	}));


/**
 * This creates a Model Adapter Type which mixes in Core Model into a MST Type
 * contructs props for the MST model dynamically based on passed white list attributes
 *
 * @param {object} env environment parameters passed to the Type
 * @param {object} env.whitelistAttrs white list attributes allowed for the Model
 * @param {object} env.options any options needed for preparing the Model
 * @param {Array} [modelList] an array of additional models needed for preparing the Model
 */
export function prepareModelAdapterType(env, modelList) {
	const blocked = ['init', 'constructor', 'set', 'toJSON'];
	const coreModel = new CoreModelClass({}, env.options);

	const isFunction = (v) => {
		return typeof v === 'function';
	};

	const isArray = (v) => {
		return Array.isArray(v);
	};

	const isObject = (v) => {
		// null is typeof object
		return v && typeof v === 'object';
	};

	return ModelAdapter
		.props(constructProps(env))
		.actions(self => {
			adoptFunctions(coreModel, self, blocked, true); //these functions are not 'mst actions'
			if (modelList && modelList.length > 0) {
				modelList.map((model) => {
					adoptFunctions(model.newInstance, self, model.methodList, model.isBlocked);
				});
			}
			return {
				afterCreate: function () {
					self.whitelistAttrs = getEnv(self).whitelistAttrs; //allow legacy toJSON to work
				},
				set: function (values, options) { //expose set as an action
					if (values instanceof _LegacyEvent$1) {
						applyPatch(self, values.value);
					} else {
						coreModel.set.call(self, values, options);
					}
				},
				toJSON: function (additionalAttrs, excludedAttrs) {
					const obj = {};
					const getJson = function (value) {
						let json;
						if (!value) {
							json = value;
						} else {
							if (value.toJSON && isFunction(value.toJSON) && !value.$mobx) {
								// Call Model toJSON
								json = value.toJSON();
							} else if (value.toJSON && isFunction(value.toJSON) && value.$mobx && !value.map) {
								// Call mobx toJSON
								json = value.toJSON();
							} else if (value.$mobx && value.map && isArray(value.map((item) => item))) {
								// Convert to an array by using mobx map
								value = value.map((item) => item);
								// ES6 map
								json = value.map((v) => {
									return getJson(v);
								});
							} else if (isArray(value)) {
								json = value.map((v) => {
									return getJson(v);
								});
							} else if (isObject(value)) {
								// Clone object
								json = { ...value };
							} else {
								json = value;
							}
						}
						return json;
					};
					let filteredWhitelist = self.whitelistAttrs;
					if (excludedAttrs) {
						filteredWhitelist = self.whitelistAttrs.filter(attr => excludedAttrs.indexOf(attr) === -1);
					}
					filteredWhitelist.forEach(attr => {
						if (self.runtimeAttrs.indexOf(attr) !== -1) {
							if (self._persistedRuntimeAttrs[attr] !== undefined) {
								obj[attr] = getJson(self._persistedRuntimeAttrs[self.resolvePropertyName(attr)]);
							}
						} else {
							if (self[attr] !== undefined) {
								obj[attr] = getJson(self[self.resolvePropertyName(attr)]);
							}
						}
					});
					if(additionalAttrs) {
						if(!isArray(additionalAttrs)) {
							additionalAttrs = [additionalAttrs];
						}
						additionalAttrs.forEach(attr => {
							if (self[self.resolvePropertyName(attr)] !== undefined) {
								obj[attr] = getJson(self[self.resolvePropertyName(attr)]);
							}
						});
					}
					return obj;
				}
			};
		});
}

/**
 * This creates a Model Adapter instance
 *
 * @param {object} model the JSON spec for the model
 * @param {object} env the environment parameters used for creating the Model
 * @param {object} env.whitelistAttrs white list attributes allowed for the Model
 * @param {object} env.options any options needed for preparing the Model
 * @param {Array} [modelList] an array of additional models needed for preparing the Model
 */
export function createModelAdapter(model, env, modelList) {
	const PreparedModelAdapterType = prepareModelAdapterType(env, modelList);
	return PreparedModelAdapterType.create(model, env);
}


// WEBPACK FOOTER //
// ./src/js/core/ModelAdapter.js